<template>
  <div
    ref="ckeditorParent"
    class="ck-wrapper"
    :class=" { 'has-error': $v.inputValue.$error, readonly }">
    <div
      v-if="label"
      class="ckeditor-label">
      <div>
        <span>{{ label }}</span>
        <span v-if="!required" id="field-required"> ({{ 'admin_marketing_optional' | translate }})</span>
      </div>
    </div>
    <ckeditor
      :id="id"
      ref="ckeditor"
      :value="inputValue"
      :editor="editor"
      :config="editorConfig"
      :required="required"
      @input="onInput"
      @ready="onReady" />
    <div
      v-if="showUploadingMessage"
      class="uploading-msg"
      :class="{ 'image-upload-pending': isUploadCheckPending }">
      {{ 'general_images_uploading' | translate }}
    </div>
    <div
      v-if="clientErrors.length"
      class="input-errors">
      <span class="client-errors">
        {{ clientErrors.join('\n') }}
      </span>
    </div>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import CKEditor from '@ckeditor/ckeditor5-vue2';
import ClassicEditor from '@emonitorag/ckeditor5-build';
import { validationMixin } from 'vuelidate';
import sanitizedContent from '@/mixins/sanitizedContent';
import { helpers } from '@/utility';

export default {
  name: 'MdtCkeditor',
  components: {
    ckeditor: CKEditor.component,
  },
  mixins: [validationMixin, sanitizedContent],
  props: {
    value: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    toolbar: {
      type: [Object, Array],
      default: () => ({
        items: [
          'heading', 'fontFamily', 'fontSize',
          '|',
          'bold', 'italic', 'underline', 'strikethrough',
          'fontColor', 'fontBackgroundColor',
          'superscript', 'subscript',
          'numberedList', 'bulletedList',
          '|',
          'link', 'uploadImage', 'htmlEmbed',
          '|',
          'undo', 'redo',
        ],
        shouldNotGroupWhenFull: true,
      }),
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    height: {
      type: String,
      default: '',
    },
    id: {
      type: String,
      default: () => helpers.generateUniqueId(),
    },
    imgWidth: {
      type: String,
      default: '70px',
    },
  },
  data() {
    const toolbarItems = Array.isArray(this.toolbar) ? this.toolbar : this.toolbar.items;
    const editorConfig = {
      placeholder: this.placeholder,
      toolbar: this.toolbar,
      htmlEmbed: {
        sanitizeHtml: (inputHtml) => ({
          html: this.sanitizedContent(inputHtml),
          hasChanged: true,
        }),
      },
    };

    if (toolbarItems.includes('uploadImage')) {
      const { baseBackendUrl } = this.$store.getters;
      const userToken = this.$store.state.auth.user.token;
      // Example, name.melon.rent -> name, localhost:8000 -> rent
      const tenantName = window.location.host.split('.')[1] ? window.location.host.split('.')[0] : 'rent';

      editorConfig.simpleUpload = {
        uploadUrl: `${baseBackendUrl}/api/v1/ckeditor-image-upload`,
        headers: {
          Authorization: `Bearer ${userToken}`,
          'Tenant-Name': tenantName,
        },
      };
    }

    return {
      toolbarItems,
      inputValue: this.value,
      editor: ClassicEditor,
      editorConfig,
      firstEmit: true,
      isUploadingImages: false,
      isUploadCheckPending: false,
      isInitializing: true,
    };
  },
  computed: {
    clientErrors() {
      const errors = [];
      if (this.$v.inputValue.$dirty) {
        if (!this.$v.inputValue.required) {
          errors.push(this.$options.filters.translate('general_field_is_required'));
        }
      }
      return errors;
    },
    showUploadingMessage() {
      const items = Array.isArray(this.toolbar) ? this.toolbar : this.toolbar.items;
      return this.isUploadingImages && items.includes('uploadImage');
    },
  },
  validations() {
    return {
      inputValue: {
        required: this.required ? required : false,
      },
    };
  },
  watch: {
    value(value) {
      this.inputValue = value;
    },
    height() {
      // set ckeditor height if editor instance and height prop available
      if (!!this.editorInstance && helpers.isNotUndefined(this.height)) this.setHeight(this.height);
    },
    readonly() {
      this.setReadOnly();
    },
  },
  methods: {
    setTouched() {
      this.$v.inputValue.$touch();
    },
    isValid() {
      this.setTouched();
      const isValid = !this.$v.inputValue.$error;
      this.isUploadCheckPending = this.showUploadingMessage;
      return isValid && !this.showUploadingMessage;
    },
    onInput(value) {
      if (this.isInitializing) {
        return;
      }

      // Fixes issue when ckeditor calls onInput() on init if it's empty
      const isEmptyInit = this.firstEmit && !this.inputValue && !value;

      if (!isEmptyInit) {
        this.$emit('mdtDataChanged');
        this.setTouched();
        this.$emit('input', value);
      }

      this.firstEmit = false;
      this.inputValue = value;
    },
    checkImagesUploading() {
      if (!this.editorInstance || !this.editorInstance.model) return;

      const root = this.editorInstance.model.document.getRoot();
      let uploading = false;

      const walk = (node) => {
        if (uploading) return;

        if (node.name === 'imageBlock' || node.name === 'imageInline') {
          const uploadId = node.getAttribute('uploadId');

          if (uploadId) {
            uploading = true;
            return;
          }
        }

        if (node.getChildren) {
          const children = Array.from(node.getChildren());
          for (let i = 0; i < children.length; i++) {
            walk(children[i]);
            if (uploading) break;
          }
        }
      };

      walk(root);
      this.isUploadingImages = uploading;
      this.isUploadCheckPending = false;
    },
    getInstance() {
      return this.$refs.ckeditor.$_instance;
    },
    onReady() {
      /* eslint-disable no-underscore-dangle, no-param-reassign */
      this.editorInstance = this.getInstance();
      if (this.editorInstance) {
        this.setReadOnly();
        this.bindEvents();
        this.ckEditorImageConfig();
        // Fix: In nested modals, the CKEditor editable is a DOM child of the outer modal's
        // vue-scroll wrapper (overflow:hidden), even though the inner modal is position:fixed
        // visually. CKEditor's Rect.getVisible() walks the DOM ancestor chain and intersects
        // the selection range rect with the outer vue-scroll's getBoundingClientRect().
        // Because the inner modal (fixed) can extend beyond the outer modal's scroll area,
        // that intersection is null → getOptimalPosition() hits its early return at line 135
        // ("if target got cropped by ancestors") → returns null → balloon placed at -99999px.
        //
        // Fix: wrap the position target as a plain object {top,right,bottom,left,width,height}.
        // Rect.getVisible() checks source.parentNode / source.commonAncestorContainer — both
        // undefined on a plain object — so the while-loop never runs and the rect is returned
        // as-is, bypassing the broken DOM-walk clipping.
        const linkUI = this.editorInstance.plugins.get('LinkUI');
        if (linkUI && linkUI._getBalloonPositionData) {
          const origGetPosData = linkUI._getBalloonPositionData.bind(linkUI);
          linkUI._getBalloonPositionData = () => {
            const data = origGetPosData();
            if (data && data.target) {
              const rawTarget = data.target;
              data.target = () => {
                const src = typeof rawTarget === 'function' ? rawTarget() : rawTarget;
                if (!src) return src;
                const r = src.getBoundingClientRect ? src.getBoundingClientRect() : src;
                return {
                  top: r.top,
                  right: r.right,
                  bottom: r.bottom,
                  left: r.left,
                  width: r.width,
                  height: r.height,
                };
              };
            }
            return data;
          };
        }
      }
      /* eslint-enable */
      this.$emit('ready');
    },
    ckEditorImageConfig() {
      // prevent error in cases when uploadImage is not in the toolbar
      if (this.toolbarItems.includes('uploadImage')) {
        this.editorInstance.model.document.on('change:data', () => {
          this.checkImagesUploading();
        });
      }

      const { schema } = this.editorInstance.model;
      const { conversion } = this.editorInstance;

      // 1. Extend schema to allow styling
      schema.extend('imageBlock', { allowAttributes: ['imgStyle'] });
      schema.extend('imageInline', { allowAttributes: ['imgStyle'] });

      // 2. Upcast: import style attribute from <img>
      conversion.for('upcast').attributeToAttribute({
        view: {
          name: 'img',
          key: 'style',
        },
        model: 'imgStyle',
      });

      // 3. Downcast: apply style to <img> tag inside view
      conversion.for('downcast').add((dispatcher) => {
        const applyStyleToImage = (evt, data, conversionApi) => {
          const viewWriter = conversionApi.writer;
          const viewElement = conversionApi.mapper.toViewElement(data.item);

          if (!viewElement) return;

          const viewImg = viewElement.is('element', 'img')
            ? viewElement
            : Array.from(viewElement.getChildren())
              .find((child) => child.is('element', 'img'));

          if (viewImg) {
            if (data.attributeNewValue) {
              viewWriter.setAttribute('style', data.attributeNewValue, viewImg);
            } else {
              viewWriter.removeAttribute('style', viewImg);
            }
          }
        };

        dispatcher.on('attribute:imgStyle:imageBlock', applyStyleToImage);
        dispatcher.on('attribute:imgStyle:imageInline', applyStyleToImage);
      });

      // 4. Recursively apply style to image nodes
      const applyDefaultStyleToImages = (node) => {
        if (!node || !node.is || !node.is('element')) return;

        if (
          (node.name === 'imageBlock' || node.name === 'imageInline')
          && !node.getAttribute('imgStyle')
        ) {
          this.editorInstance.model.change((writer) => {
            writer.setAttribute('imgStyle', `width: ${this.imgWidth};`, node);
          });
        }

        const children = Array.from(node.getChildren());
        for (let i = 0; i < children.length; i++) {
          applyDefaultStyleToImages(children[i]);
        }
      };

      // 5. Apply styles on content changes
      this.editorInstance.model.document.on('change:data', (evt, batch) => {
        if (!batch.isUndo) {
          const root = this.editorInstance.model.document.getRoot();
          applyDefaultStyleToImages(root);
        }
      });

      // 6. Set initial content
      try {
        this.editorInstance.setData(this.value);
        this.isInitializing = false;
      } catch (err) {
        console.error('CKEditor setData() failed:', err);
      }
    },
    setReadOnly() {
      if (this.readonly) {
        this.editorInstance.enableReadOnlyMode('lockId');
      } else {
        this.editorInstance.disableReadOnlyMode('lockId');
      }
    },
    setHeight(height) {
      const ckeditorRef = this.$refs.ckeditorParent;
      if (ckeditorRef) {
        const ckEditorEditableInline = ckeditorRef.querySelector('.ck-wrapper .ck-editor__main');
        if (ckEditorEditableInline) ckEditorEditableInline.setAttribute('style', `height: ${height}`);
      }
    },
    bindEvents() {
      // set ckeditor height from prop if available
      if (helpers.isNotUndefined(this.height)) this.setHeight(this.height);
    },
  },
};
</script>

<style lang="scss">
:root {
  --ck-border-radius: 4px !important; // border radius for ckeditor wrapper
  // Balloon/dropdown panels need to appear above MdtModal (z-index: 1002).
  --ck-z-panel: 1003 !important;
  --ck-color-engine-placeholder-text: #{$color-back-basic} !important;
  --ck-color-base-border: #{$border-color} !important;
  --ck-color-toolbar-background: $color-back-primary !important;
}
</style>

<style lang="scss" scoped>
.ckeditor-label {
  margin-bottom: 8px;
  color: $color-text-secondary;
  font-size: 14px;
  line-height: 14px;
}

.ck-wrapper {
  line-height: 1.5;

  .uploading-msg {
    font-size: 12px;
    color: $color-text-primary;
    padding-top: 4px;

    &.image-upload-pending {
      color: $color-danger;
    }
  }

  &.readonly {
    ::v-deep {
      .ck.ck-toolbar {
        border-color: rgba($border-color, 0.3) !important;
        pointer-events: none;
      }

      .ck.ck-editor__editable_inline {
        border-color: rgba($border-color, 0.3) !important;
      }

      .ck-editor__editable:not(.ck-focused):hover {
        border-color: rgba($border-color, 0.3) !important;
      }
    }
  }

  ::v-deep {
    .ck.ck-content {
      & > p,
      & > ul,
      & > ol,
      & > blockquote,
      & > pre {
        margin-bottom: var(--ck-spacing-large);
      }
    }

    .ck-editor__editable_inline {
      height: 100%;
      min-height: 122px;
      max-height: 500px;
    }

    .ck-editor__editable:not(.ck-focused):hover {
      border-color: $color-text-secondary !important;
    }

    .ck-content.ck-focused {
      border: 1px solid $border-color !important;
      box-shadow: 0 0 4px $color-theme inset !important;
    }

    // Adjust toolbars first button tooltip
    .ck.ck-toolbar .ck-button:first-child .ck-tooltip__text {
      left: -15%;

      &::after {
        left: 15%;
      }
    }

    ul, ol {
      padding-inline-start: 40px;
    }

    figure {
      margin: 0 0 var(--ck-spacing-large) 0;
    }

    // Disable sticky toolbar — inside a modal with a custom scroll container
    // the toolbar floats over unrelated content above the editor.
    .ck-sticky-panel__content_sticky {
      position: static !important;
      top: auto !important;
      width: auto !important;
    }
  }

  &.has-error {
    color: $color-danger;

    ::v-deep {
      .ck-editor__editable:not(.ck-focused):hover {
        border-color: $color-danger !important;
      }

      .ck-content {
        background-color: rgba($color-danger, 0.05) !important;
        border: 2px solid $color-danger !important;
        box-shadow: 0 0 2px rgba($color-danger, 0.4) !important;
      }
    }
  }

  .input-errors {
    padding-top: 4px;
    font-size: 12px;
    font-weight: $font-weight-normal;
    white-space: pre-line;
  }
}
</style>
